import React, { useEffect, useState } from "react";
import {
  Card,
  CardBody,
  Row,
  Col,
  FormGroup,
  Label,
  Button,
} from "reactstrap";
import { useMutation, useQuery } from "@apollo/client";
import { useHistory } from "react-router-dom";
import { useForm } from "react-hook-form";
import {
  HOSPITAL_DETAIL_QUERY,
  HOSPITAL_EDIT_MUTATION,
  HOSPITAL_LIST_QUERY,
} from "../../config/Queries";
import { toast } from "react-toastify";

function HospitalEdit({ match }) {
  const id = parseInt(match.params.id, 10);
  const history = useHistory();
  const { data, loading } = useQuery(HOSPITAL_DETAIL_QUERY, {
    variables: { id },
  });
  const [logoFile, setLogoFile] = useState(null);
  const [newImageFiles, setNewImageFiles] = useState([]);
  const [keptImages, setKeptImages] = useState([]);
  const { register, handleSubmit, reset } = useForm();
  const [editHospitalMutation] = useMutation(HOSPITAL_EDIT_MUTATION, {
    refetchQueries: [
      { query: HOSPITAL_LIST_QUERY },
      { query: HOSPITAL_DETAIL_QUERY, variables: { id } },
    ],
  });

  useEffect(() => {
    const hospital = data?.viewHospital;
    if (!hospital) {
      return;
    }

    reset({
      name: hospital.name || "",
      tags: hospital.tags || "",
      greeting: hospital.greeting || "",
      address: hospital.address || "",
      phone: hospital.phone || "",
      latitude: hospital.latitude ?? "",
      longitude: hospital.longitude ?? "",
    });
    setKeptImages(
      hospital.images ? hospital.images.split("|").filter(Boolean) : []
    );
  }, [data, reset]);

  const removeKeptImage = (imageUrl) => {
    setKeptImages((prev) => prev.filter((url) => url !== imageUrl));
  };

  const onSubmit = async (formData) => {
    try {
      const {
        data: { editHospital },
      } = await editHospitalMutation({
        variables: {
          id,
          name: formData.name,
          tags: formData.tags || null,
          greeting: formData.greeting || null,
          address: formData.address || null,
          phone: formData.phone || null,
          latitude: formData.latitude ? parseFloat(formData.latitude) : null,
          longitude: formData.longitude ? parseFloat(formData.longitude) : null,
          logo: logoFile,
          newImages: newImageFiles.length ? newImageFiles : null,
          keptImages,
        },
      });

      if (!editHospital?.ok) {
        toast.error(editHospital?.error || "병원 수정에 실패했습니다.");
        return;
      }

      toast.success("병원 수정이 완료되었습니다.");
      history.push(`/admin/hospitals/${id}`);
    } catch (e) {
      console.log(e);
      toast.error("병원 수정 중 오류가 발생했습니다.");
    }
  };

  if (loading) {
    return null;
  }

  const hospital = data?.viewHospital;

  return (
    <div className="content">
      <form onSubmit={handleSubmit(onSubmit)}>
        <Card>
          <div className="card-heading">병원 정보 수정</div>
          <CardBody>
            <FormGroup row>
              <Label sm={2}>병원명</Label>
              <Col sm={10}>
                <input
                  className="form-control"
                  ref={register({ required: true })}
                  name="name"
                />
              </Col>
            </FormGroup>
            <FormGroup row>
              <Label sm={2}>태그</Label>
              <Col sm={10}>
                <input className="form-control" ref={register} name="tags" />
              </Col>
            </FormGroup>
            <FormGroup row>
              <Label sm={2}>인사말</Label>
              <Col sm={10}>
                <textarea
                  className="form-control"
                  style={{ minHeight: 120 }}
                  ref={register}
                  name="greeting"
                />
              </Col>
            </FormGroup>
            <FormGroup row>
              <Label sm={2}>주소</Label>
              <Col sm={10}>
                <input className="form-control" ref={register} name="address" />
              </Col>
            </FormGroup>
            <FormGroup row>
              <Label sm={2}>전화번호</Label>
              <Col sm={10}>
                <input className="form-control" ref={register} name="phone" />
              </Col>
            </FormGroup>
            <FormGroup row>
              <Label sm={2}>위도</Label>
              <Col sm={4}>
                <input
                  className="form-control"
                  ref={register}
                  name="latitude"
                  type="number"
                  step="any"
                />
              </Col>
              <Label sm={2}>경도</Label>
              <Col sm={4}>
                <input
                  className="form-control"
                  ref={register}
                  name="longitude"
                  type="number"
                  step="any"
                />
              </Col>
            </FormGroup>
            <FormGroup row>
              <Label sm={2}>로고</Label>
              <Col sm={10}>
                {hospital?.logo && !logoFile && (
                  <div className="m-b-10">
                    <img
                      src={hospital.logo}
                      alt="현재 로고"
                      style={{
                        width: 120,
                        height: 120,
                        objectFit: "cover",
                        borderRadius: 4,
                      }}
                    />
                  </div>
                )}
                <input
                  type="file"
                  accept="image/*"
                  className="form-control"
                  onChange={(e) => setLogoFile(e.target.files?.[0] || null)}
                />
              </Col>
            </FormGroup>
            <FormGroup row>
              <Label sm={2}>병원 사진</Label>
              <Col sm={10}>
                {keptImages.length > 0 && (
                  <Row className="m-b-15">
                    {keptImages.map((imageUrl) => (
                      <Col xs={6} md={3} key={imageUrl} className="m-b-10">
                        <div style={{ position: "relative" }}>
                          <img
                            src={imageUrl}
                            alt="병원 사진"
                            style={{
                              width: "100%",
                              height: 120,
                              objectFit: "cover",
                              borderRadius: 4,
                            }}
                          />
                          <Button
                            type="button"
                            color="danger"
                            size="sm"
                            style={{
                              position: "absolute",
                              top: 4,
                              right: 4,
                            }}
                            onClick={() => removeKeptImage(imageUrl)}
                          >
                            삭제
                          </Button>
                        </div>
                      </Col>
                    ))}
                  </Row>
                )}
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  className="form-control"
                  onChange={(e) =>
                    setNewImageFiles(
                      e.target.files ? Array.from(e.target.files) : []
                    )
                  }
                />
                {newImageFiles.length > 0 && (
                  <small className="text-muted">
                    새로 추가할 사진 {newImageFiles.length}개
                  </small>
                )}
              </Col>
            </FormGroup>
          </CardBody>
        </Card>
        <Row>
          <Col xs={4}>
            <Button
              type="button"
              className="btn-white"
              onClick={() => history.push(`/admin/hospitals/${id}`)}
            >
              <i className="fa fa-list"></i> 상세
            </Button>
          </Col>
          <Col xs={4} className="text-center">
            <Button type="submit" className="btn-inverse">
              <i className="fa fa-check"></i> 저장
            </Button>
          </Col>
        </Row>
      </form>
    </div>
  );
}

export default HospitalEdit;
